import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { KEY_MOMENTS } from "@/pages/Landing";
import { RegisterForm } from "@/components/auth/RegisterForm";
import { LoginForm } from "@/components/auth/LoginForm";
import { PageBackground } from "@/components/PageBackground";

export default function Auth() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [role, setRole] = useState(params.get("role") === "landlord" ? "landlord" : "tenant");
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [regData, setRegData] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const go = (user) => {
    navigate(user.role === "tenant" ? "/tenant" : user.role === "landlord" ? "/landlord" : "/admin");
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", loginData);
      loginWithToken(data.token, data.user);
      toast.success("Welcome back");
      go(data.user);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const referral_code = params.get("ref") || undefined;
      const { data } = await api.post("/auth/register", { ...regData, role, referral_code });
      loginWithToken(data.token, data.user);
      toast.success("Account created");
      go(data.user);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    localStorage.setItem("rm_pending_role", role);
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div>
      <PageBackground variant={0} />
      <Navbar />
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="text-center mb-8">
          <img src="/brand-logo.png" alt="MyRentalMatch" className="h-14 w-auto mx-auto mb-4" />
          <h1 className="font-display text-2xl font-semibold tracking-tight">Verified once. Offers forever.</h1>
          <p className="text-muted-foreground mt-1">Sign in to get pitched by landlords.</p>
          <div className="flex items-center justify-center gap-3 mt-4">
            <div className="flex -space-x-2">
              {KEY_MOMENTS.map((m) => (
                <img key={m.url} src={m.url} alt={m.alt} className="w-8 h-8 rounded-full object-cover border-2 border-white" />
              ))}
            </div>
            <span className="text-xs text-muted-foreground">Join renters getting their keys 🎉</span>
          </div>
        </div>

        <div className="bg-white border border-border rounded-sm p-6">
          <Tabs defaultValue="register">
            <TabsList className="grid grid-cols-2 w-full mb-6">
              <TabsTrigger value="register" data-testid="tab-register">Sign up</TabsTrigger>
              <TabsTrigger value="login" data-testid="tab-login">Log in</TabsTrigger>
            </TabsList>

            <TabsContent value="register">
              <RegisterForm role={role} setRole={setRole} regData={regData} setRegData={setRegData} busy={busy} onSubmit={doRegister} />
            </TabsContent>

            <TabsContent value="login">
              <LoginForm loginData={loginData} setLoginData={setLoginData} busy={busy} onSubmit={doLogin} />
            </TabsContent>
          </Tabs>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-muted-foreground">or</span></div>
          </div>
          <Button variant="outline" className="w-full" data-testid="google-login" onClick={googleLogin}>
            Continue with Google
          </Button>
        </div>
      </div>
    </div>
  );
}
